import {
  OrderGetResponseType,
  SupplierRelationshipGetResponseType,
  SupplierRelationshipFulfillmentType,
} from "@sellar-io/api-v2/types";
import { useLiveQuery } from "dexie-react-hooks";
import { ReactNode, useEffect, useEffectEvent, useReducer } from "react";
import type { ISODateString } from "../types/isoDate";
import { useSearchParams } from "react-router";
import { v4 } from "uuid";
import db from "../db";
import { useDraftOrders } from "./api/v2/useDraftOrders";
import { useSellarCurrentBusiness } from "./useSellarCurrentBusiness.shared";
import { useSellarUser } from "./index";
import { isPromotionalOrderItem } from "../pages/stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import {
  BasketContextActionEnum,
  initialState,
  reducer,
  BasketContext,
} from "./useBasketContext.shared";

export interface ProductItem extends Omit<
  NonNullable<ContextOrderBasketType["OrderItems"]>[number]["Product"],
  "ProductPriceTier"
> {
  ProductPriceTier: {
    PriceTierId: number;
    price: number;
    fullPrice: number;
    discountable: boolean;
  } | null;
}

export interface BasketOrderItem extends Omit<
  OrderGetResponseType["OrderItems"][number],
  "id"
> {
  id: number | undefined;
  promotionRewardItemId?: number;
}

// This will be more defined when reducer is removed
// We dont make this type a partial, as once in checkout, we want to ensure all of these values are defined
export interface OrderBasketType extends Omit<
  OrderGetResponseType,
  | "OrderItems"
  | "createdAt"
  | "id"
  | "updatedAt"
  | "DeliveryAddress"
  | "InvoiceAddress"
> {
  id: number;
  updatedAt: Date | null;
  OrderItems: BasketOrderItem[];
  SupplierId: number;
  RetailerId: number;
  OrderDiscounts?: {
    PriceTierDiscountId: number;
  }[];
  OrderComments?: { message: string }[];
}
// Have to do partial for now as the reducer sets EVERYTHING in the order basket, so everything potentially undefined until we refactor
// Omitting order items so that we can make id optional as will only exist after creation now
export interface ContextOrderBasketType extends Partial<
  Omit<OrderBasketType, "OrderItems" | "RetailerId">
> {
  OrderItems?: BasketOrderItem[];
  RetailerId: number | null;
  idempotencyKey?: string;
  _fromOrderFlowToken?: boolean;
  sdBasketId?: number;
  sdBasketUuid?: string;
  expectedDeliveryDate?: ISODateString | null;
  orderCutoffDate?: string | null;
}
type BasketContextState = {
  temporaryCartId: string;
  order: ContextOrderBasketType | null;
  connection?: SupplierRelationshipGetResponseType;
  loading?: boolean;
  modifyOrderItem?: (params: {
    productItem: ProductItem;
    quantity: number;
    applyEligiblePromotionRewards: (params: {
      order: ContextOrderBasketType;
    }) => ContextOrderBasketType;
  }) => void;
};

export type BasketContextAction =
  | { type: BasketContextActionEnum.RESET }
  | {
      type: BasketContextActionEnum.CHANGE_DISCOUNT;
      discount: {
        list: {
          PriceTierDiscountId: number;
        }[];
        totalVolumeDiscountAmount: number;
      };
    }
  | {
      type: BasketContextActionEnum.CHANGE_VAT_PERCENTAGE;
      basketVatPercentage: number;
    }
  | {
      type: BasketContextActionEnum.CHANGE_DELIVERY_CHARGE;
      deliveryCharge: number;
    }
  | {
      type: BasketContextActionEnum.CHANGE_CONNECTION;
      connection: SupplierRelationshipGetResponseType;
    }
  | {
      type: BasketContextActionEnum.CHANGE_SUPPLIER_RETAILER_IDS;
      RetailerId?: number;
      SupplierId?: number;
    }
  | {
      type: BasketContextActionEnum.CHANGE_CURRENCY;
      currency: "gbp";
    }
  | {
      type: BasketContextActionEnum.CHANGE_FULFILLMENT_TYPE;
      fulfillmentType: SupplierRelationshipFulfillmentType;
    }
  | {
      type: BasketContextActionEnum.CHANGE_ORDER;
      order: Partial<ContextOrderBasketType> | null;
    }
  | {
      type: BasketContextActionEnum.CHANGE_LOADING;
      loading: boolean;
    };

export const BasketProvider = (props: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    order: {
      RetailerId: 0,
    },
  });
  const { deleteOrder: deleteDraftOrder } = useDraftOrders();
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const [searchParams] = useSearchParams();
  const orderFlowToken = searchParams.get("orderFlowToken");
  const isSignedInUser = Boolean(user);

  const draftOrder = useLiveQuery(() => {
    if (!state.order?.SupplierId || state.order.id) return;

    return db.draftOrders.get({
      // Set to zero for unauthenticated orders so we can get this working
      RetailerId: state.order.RetailerId ?? 0,
      SupplierId: state.order.SupplierId,
      //initiator used to know who the draft order belongs to
      initiator: business?.Supplier ? "supplier" : "retailer",
    });
  }, [state.order?.SupplierId, state.order?.id, business]);

  const hydrateDraftOrder = useEffectEvent(() => {
    let newOrder = draftOrder
      ? draftOrder
      : {
          ...state.order,
          ...(state.order?.OrderItems
            ? { OrderItems: state.order.OrderItems }
            : { OrderItems: [] }),
          RetailerId: state.order?.RetailerId ?? business?.Retailer?.id ?? 0,
        };

    newOrder = {
      ...newOrder,
      RetailerId: state.order?.RetailerId ?? 0,
      OrderDiscounts: [],
    };

    dispatch({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: newOrder,
    });
  });

  useEffect(() => {
    hydrateDraftOrder();
  }, [draftOrder]);

  useEffect(() => {
    dispatch({
      type: BasketContextActionEnum.RESET,
    });
  }, [business?.id]);

  // Recovery effect for orderFlowToken flow after signup
  // Loads saved order from draftOrders when business changes and basket is empty
  useEffect(() => {
    // Only run if we have a buyer and no order in state (after reset)
    if (!business?.Retailer?.id || state.order?.SupplierId) {
      return;
    }

    // Check for saved order in draftOrders with the new retailerId
    db.draftOrders
      .where({
        RetailerId: business.Retailer.id,
        initiator: "retailer",
      })
      .first()
      .then((savedOrder) => {
        // Restore orderFlowToken orders (marked with _fromOrderFlowToken)
        // These are existing orders saved to survive the business change
        if (savedOrder?.id && savedOrder?._fromOrderFlowToken) {
          dispatch({
            type: BasketContextActionEnum.CHANGE_ORDER,
            order: savedOrder,
          });
        }
      });
  }, [business?.Retailer?.id, state.order?.SupplierId]);

  // Re-hydrate unauthenticated orderFlowToken basket after RESET (e.g. back-navigation).
  // After RESET, SupplierId is undefined so BasketProvider's draftOrder live query never
  // fires. This effect reads the saved draft directly from IndexedDB when the token is
  // present but the basket has been cleared.
  useEffect(() => {
    if (!orderFlowToken || isSignedInUser || state.order?.SupplierId) return;

    db.draftOrders
      .where({ initiator: "retailer" })
      .filter((o) => !!o._fromOrderFlowToken)
      .first()
      .then((savedOrder) => {
        if (savedOrder?.id) {
          dispatch({
            type: BasketContextActionEnum.CHANGE_ORDER,
            order: savedOrder,
          });
        }
      });
  }, [orderFlowToken, isSignedInUser, state.order?.SupplierId]);

  //only id on the Product object needs to be defined to update quantity
  const modifyOrderItem: BasketContextState["modifyOrderItem"] = ({
    productItem,
    quantity,
    applyEligiblePromotionRewards,
  }) => {
    const productId = productItem.id;

    if (
      typeof productItem.maxOrderQuantity === "number" &&
      quantity > productItem.maxOrderQuantity
    ) {
      throw new Error("Quantity cannot exceed maximum order quantity");
    }

    if (quantity < 0) throw new Error("Quantity cannot be less than 0");

    let order = state.order;

    if (!order?.SupplierId) throw new Error("No supplier id on order");
    if (!order.RetailerId && order.RetailerId !== 0)
      throw new Error("No buyer id on order");

    const index = order.OrderItems?.findIndex(
      (orderItem) =>
        orderItem.Product.id === productId &&
        !isPromotionalOrderItem(orderItem),
    );

    //silently return if item does not exist and desired quantity is 0
    if (index === -1 && quantity === 0) return;

    //if new quantity is 0, remove the item
    if (quantity === 0 && order.OrderItems?.length) {
      const nextOrder = {
        ...order,
        OrderItems: order.OrderItems.filter((orderItem) => {
          const validOrderItem =
            orderItem.Product.id === productId &&
            !isPromotionalOrderItem(orderItem);

          return !validOrderItem;
        }),
      };

      if (nextOrder.OrderItems.length === 0 && !nextOrder.id) {
        dispatch({
          type: BasketContextActionEnum.CHANGE_ORDER,
          order: nextOrder,
        });

        // Use the already-narrowed ids from `order` (clone only changed OrderItems).
        return deleteDraftOrder({
          SupplierId: order.SupplierId,
          RetailerId: order.RetailerId,
          initiator: "retailer",
        });
      }

      const orderWithPromotionApplied = applyEligiblePromotionRewards({
        order: nextOrder,
      });

      if (orderWithPromotionApplied.id) {
        // For existing orders (with id), only dispatch to context
        // Don't save to draftOrders (unless unauthenticated - handled below)
        dispatch({
          type: BasketContextActionEnum.CHANGE_ORDER,
          order: orderWithPromotionApplied,
        });
        // Save to draftOrders for orderFlowToken flow (existing orders with id)
        // This allows recovery after business change during signup
        // Uses the actual RetailerId from the order
        // Only save if this is from orderFlowToken (has _fromOrderFlowToken marker)
        if (orderWithPromotionApplied._fromOrderFlowToken) {
          return db.draftOrders.put({
            ...orderWithPromotionApplied,
            RetailerId: orderWithPromotionApplied.RetailerId ?? 0,
            initiator: "retailer",
          });
        }
        return;
      } else {
        return db.draftOrders.put({
          ...orderWithPromotionApplied,
          RetailerId: orderWithPromotionApplied.RetailerId,
          idempotencyKey: v4(),
        });
      }
    }

    //if item already exists, replace existing item with new quantity
    if ((index || index === 0) && index !== -1 && order.OrderItems) {
      const currentItem = order.OrderItems[index];

      if (!currentItem) throw new Error("Cannot find current item");

      //if the quantity is the same, silently return
      if (currentItem.quantity === quantity) return;

      const nextOrderItems = [...order.OrderItems];
      nextOrderItems[index] = {
        ...currentItem,
        quantity,
      };
      const nextOrder = {
        ...order,
        OrderItems: nextOrderItems,
      };

      const orderWithPromotionApplied = applyEligiblePromotionRewards({
        order: nextOrder,
      });

      if (orderWithPromotionApplied.id) {
        // For existing orders (with id), dispatch to context
        dispatch({
          type: BasketContextActionEnum.CHANGE_ORDER,
          order: orderWithPromotionApplied,
        });
        // Save to draftOrders for orderFlowToken flow (existing orders with id)
        if (orderWithPromotionApplied._fromOrderFlowToken) {
          return db.draftOrders.put({
            ...orderWithPromotionApplied,
            RetailerId: orderWithPromotionApplied.RetailerId ?? 0,
            initiator: "retailer",
          });
        }
        return;
      } else {
        return db.draftOrders.put({
          ...orderWithPromotionApplied,
          RetailerId: orderWithPromotionApplied.RetailerId ?? 0,
          idempotencyKey: v4(),
        });
      }
    }

    //otherwise add new item
    if (!productItem.ProductPriceTier) {
      throw new Error("No product price tier on product, cannot add to basket");
    }

    const newOrder: ContextOrderBasketType = {
      ...order,
      //delivery charge needs to be undefined so that the delivery charge is calculated from scratch rather than using to pre-defined value.
      deliveryCharge: undefined,
      discountAmount: undefined,
      OrderItems: [
        ...(order.OrderItems ? order.OrderItems : []),
        {
          id: undefined, // should this be undefined?
          quantity,
          price: productItem.ProductPriceTier.price,
          fullPrice: productItem.ProductPriceTier.fullPrice,
          ProductId: productItem.id,
          discountable: productItem.ProductPriceTier.discountable,
          Product: {
            ...productItem,
            ProductPriceTier: productItem.ProductPriceTier,
            Parent: productItem.Parent,
          },
          promotionAppliedAt: null,
          PromotionRewardItemId: null,
        },
      ],
    };

    const orderWithPromotionApplied = applyEligiblePromotionRewards({
      order: newOrder,
    });

    if (orderWithPromotionApplied.id) {
      // For existing orders (with id), dispatch to context
      dispatch({
        type: BasketContextActionEnum.CHANGE_ORDER,
        order: orderWithPromotionApplied,
      });
      // Save to draftOrders for orderFlowToken flow (existing orders with id)
      if (orderWithPromotionApplied._fromOrderFlowToken) {
        return db.draftOrders.put({
          ...orderWithPromotionApplied,
          RetailerId: orderWithPromotionApplied.RetailerId ?? 0,
          initiator: "retailer",
        });
      }
      return;
    } else {
      return db.draftOrders.put({
        ...orderWithPromotionApplied,
        RetailerId: orderWithPromotionApplied.RetailerId,
        idempotencyKey: v4(),
      });
    }
  };

  return (
    <BasketContext.Provider
      value={[
        {
          ...state,
          modifyOrderItem,
          temporaryCartId: initialState.temporaryCartId,
        },
        dispatch,
      ]}
    >
      {props.children}
    </BasketContext.Provider>
  );
};
